<template>
	<LazyLoadWrapper>
		<footer
			v-if="footer"
			aria-labelledby="footer-heading"
			class="text-gray m-auto"
			:class="footer.footer_background_color || 'bg-secondary'"
		>
			<div class="main-wrapper justify-items-start py-16 px-8">
				<div class="flex md:flex-row w-full gap-8 flex-wrap">
					<div
						class="font-medium text-lg flex flex-col gap-5 w-full md:max-w-[calc(25%-32px)] max-w-[calc(50%-32px)]"
					>
						<span
							class="text-sm uppercase"
							:class="footer.footer_text_color || 'text-gray-darker'"
						>
							{{ footer?.title_footer1 }}</span
						>
						<div
							class="text-base flex-col gap-4 flex"
							:class="footer.footer_text_color || 'text-gray-dark'"
						>
							<StoryblokComponent
								v-for="content in footer?.footer1"
								:key="content._uid"
								:blok="content"
							/>
						</div>
					</div>
					<div
						class="font-medium text-lg flex flex-col gap-5 w-full md:max-w-[calc(25%-32px)] max-w-[calc(50%-32px)]"
					>
						<span
							class="text-sm uppercase"
							:class="footer.footer_text_color || 'text-gray-darker'"
							>{{ footer?.title_footer2 }}</span
						>
						<div
							class="text-base flex-col gap-4 flex"
							:class="footer.footer_text_color || 'text-gray-dark'"
						>
							<StoryblokComponent
								v-for="content in footer?.footer2"
								:key="content._uid"
								:blok="content"
							/>
						</div>
					</div>
					<div
						class="font-medium text-lg flex flex-col gap-5 w-full md:max-w-[calc(25%-32px)] max-w-[calc(50%-32px)] md:mt-0 mt-8"
					>
						<span
							class="text-sm uppercase"
							:class="footer.footer_text_color || 'text-gray-darker'"
							>{{ footer?.title_footer3 }}</span
						>
						<div
							class="text-base flex-col gap-4 flex"
							:class="footer.footer_text_color || 'text-gray-dark'"
						>
							<StoryblokComponent
								v-for="content in footer?.footer3"
								:key="content._uid"
								:blok="content"
							/>
						</div>
					</div>
					<div
						v-if="footerLogo"
						class="font-medium text-lg flex flex-col gap-5 md:mt-0 mt-8 w-full md:max-w-[calc(25%-32px)] max-w-[calc(50%-32px)]"
					>
						<NuxtLink :to="localePath(`/`)">
							<NuxtImg
								:src="footerLogo.filename"
								:alt="footerLogo.alt || 'logo'"
								class="logo max-w-40"
								loading="eager"
								:width="siteLogoDimensions?.width"
								:height="siteLogoDimensions?.height"
							/>
						</NuxtLink>
					</div>
				</div>
				<div
					class="flex w-full md:flex-row flex-col md:justify-between md:mt-20 my-8 gap-8"
				>
					<div
						class="font-medium text-lg flex flex-col md:w-6/12 w-auto md:mx-0 mx-auto gap-4"
					>
						<span
							class="text-sm uppercase md:text-left text-center"
							:class="footer.footer_text_color || 'text-gray-darker'"
							>{{ footer?.title_footer5 }}
						</span>
						<div class="text-base gap-6 flex">
							<StoryblokComponent
								v-for="content in footer?.footer5"
								:key="content._uid"
								:blok="content"
							/>
						</div>
					</div>
					<div
						class="font-medium text-lg flex flex-col md:w-6/12 w-auto md:mx-0 mx-auto gap-4 md:mt-0 mt-8"
					>
						<span
							class="text-sm uppercase text-left"
							:class="footer.footer_text_color || 'text-gray-darker'"
							>{{ footer?.title_footer6 }}</span
						>
						<div
							class="text-base gap-6 flex"
							:class="footer.footer_text_color || 'text-gray-dark'"
						>
							<StoryblokComponent
								v-for="content in footer?.footer6"
								:key="content._uid"
								:blok="content"
								:social-media="true"
							/>
						</div>
					</div>
				</div>
				<div
					class="text-sm font-normal opacity-80 border-t border-gray-600 w-full pt-8 text-center flex justify-center items-center"
					:class="footer.footer_text_color || 'text-gray-darker'"
				>
					© {{ currentYear }}
					<div
						class="ml-2"
						v-html="renderRichText(footer?.footer_copyright)"
					></div>
				</div>
			</div>
		</footer>
	</LazyLoadWrapper>
</template>

<script setup lang="ts">
import type { StoryblokAppLayout } from "~/types/storyblok/storyblok";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";
import { getStoryblokVersion } from "~/utils/storyblok/getStoryblokVersion";

const storyblokApi = useStoryblokApi();
const localePath = useLocalePath();
const { footerLogo, siteLogoDimensions } = useAppStore();

const footer = ref<Partial<StoryblokAppLayout>>();

const currentYear = ref<number>(new Date().getFullYear());

const { localeProperties } = useI18n();

const getFooter = async () => {
	const { data } = await storyblokApi.get("cdn/stories/layout", {
		version: getStoryblokVersion(),
		language: localeProperties.value.iso,
		resolve_links: "url"
	});

	footer.value = data.story.content;
};

onMounted(() => {
	getFooter();
});
</script>
